/** The list of documents a task is expected to arrive with, while it is being written.
 *
 *  One component for the entry grid and the Add task drawer, because three places that
 *  each build the same list would drift — and a document list that means something
 *  slightly different depending on where it was typed is worse than not having one.
 *
 *  The task drawer edits documents that already exist on a saved task, which is a
 *  different job (it has ids, and files attached to them); this is purely the draft
 *  shape both create paths post.
 */
import { useRef, useState } from 'react'

export type DraftDocument = { name: string; required: boolean }

/** Compact enough for a grid cell: "2 docs · 1 required", or a dash. */
export function documentsLabel(docs: DraftDocument[]): string {
  const named = docs.filter((d) => d.name.trim())
  if (named.length === 0) return '—'
  const required = named.filter((d) => d.required).length
  return `${named.length} doc${named.length === 1 ? '' : 's'}`
    + (required ? ` · ${required} required` : '')
}

export function DocumentsField({ value, onChange, onDone }: {
  value: DraftDocument[]
  onChange: (next: DraftDocument[]) => void
  /** Rendered as a popover in the grid, inline in the drawer; only the grid needs a
   *  way to close itself. */
  onDone?: () => void
}) {
  // Start with one empty row so the first thing the Owner sees is somewhere to type,
  // rather than a button that reveals somewhere to type.
  const [rows, setRows] = useState<DraftDocument[]>(
    value.length ? value : [{ name: '', required: true }])
  /** The panel itself, so a freshly added row's name box can be found and focused. */
  const box = useRef<HTMLDivElement>(null)

  const push = (next: DraftDocument[]) => {
    setRows(next)
    onChange(next.filter((d) => d.name.trim()))
  }

  /** Add a row and put the caret in it.
   *
   *  `autoFocus` above fires for the first row on mount and never again, so before
   *  this the only way to a second document was Tab to "Add another" - and because
   *  the new row is inserted BEFORE that button with no focus move, you then had to
   *  Shift+Tab three times back past the × and the required box to reach the name
   *  field. Enter does the whole thing.
   */
  const another = () => {
    if (rows.length >= 10) return
    setRows([...rows, { name: '', required: true }])
    // A frame, because the input does not exist until React has painted the row.
    requestAnimationFrame(() => {
      const boxes = box.current?.querySelectorAll<HTMLInputElement>('input[type=text]')
      boxes?.[boxes.length - 1]?.focus()
    })
  }

  return (
    // Escape closes it, which it never did: this field has had no key handling at
    // all, and the grid deliberately stands down for anything inside a popover - so
    // the only way out was to find "Done" by Tab.
    <div ref={box} className="space-y-1.5"
         onKeyDown={(e) => {
           if (e.key === 'Escape' && onDone) {
             e.preventDefault()
             onDone()
           }
         }}>
      {rows.map((d, i) => (
        <div key={i} className="flex items-center gap-2">
          <input className="field text-[12.5px] flex-1" value={d.name} maxLength={120}
                 type="text"
                 placeholder="GST challan" autoFocus={i === 0}
                 aria-label={`Document ${i + 1}`}
                 onKeyDown={(e) => {
                   // Enter adds the next document rather than doing nothing.
                   if (e.key !== 'Enter') return
                   e.preventDefault()
                   if (d.name.trim()) another()
                   else onDone?.()      // Enter on an empty row means "that is all"
                 }}
                 onChange={(e) => push(rows.map(
                   (x, j) => j === i ? { ...x, name: e.target.value } : x))} />
          <label className="flex items-center gap-1 text-[11.5px] text-ink-2 shrink-0">
            <input type="checkbox" checked={d.required}
                   onChange={(e) => push(rows.map(
                     (x, j) => j === i ? { ...x, required: e.target.checked } : x))} />
            required
          </label>
          {rows.length > 1 && (
            <button type="button" className="text-ink-3 hover:text-ink px-1 shrink-0"
                    aria-label={`Remove document ${i + 1}`}
                    onClick={() => push(rows.filter((_, j) => j !== i))}>×</button>
          )}
        </div>
      ))}

      <div className="flex items-center gap-2 pt-0.5">
        <button type="button" className="btn btn-sm"
                disabled={rows.length >= 10}
                onClick={another}>
          Add another
        </button>
        <span className="flex-1" />
        {onDone && (
          <button type="button" className="btn btn-sm btn-primary" onClick={onDone}>
            Done
          </button>
        )}
      </div>

      <p className="text-[11px] text-ink-3 leading-snug">
        A <b>required</b> document has to be attached before the task can be submitted.
        An optional one is only a reminder.
      </p>
    </div>
  )
}
